import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { trackPageView } from "@/lib/metaPixel";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import DashboardLayout from "@/pages/DashboardLayout";
import Dashboard from "@/pages/Dashboard";
import Services from "@/pages/Services";
import Staff from "@/pages/Staff";
import Reports from "@/pages/Reports";
import WorkingHours from "@/pages/WorkingHours";
import Blockouts from "@/pages/Blockouts";
import Reservations from "@/pages/Reservations";
import Takvim from "@/pages/Takvim";
import Settings from "@/pages/Settings";
import WhatsAppSettings from "@/pages/WhatsAppSettings";
import Subscription from "@/pages/Subscription";
import CashRegister from "@/pages/CashRegister";
import BookingPage from "@/pages/BookingPage";
import BookingManage from "@/pages/BookingManage";
import Businesses from "@/pages/Businesses";
import Landing from "@/pages/Landing";
import Iletisim from "@/pages/legal/Iletisim";
import MesafeliSatis from "@/pages/legal/MesafeliSatis";
import Teslimat from "@/pages/legal/Teslimat";
import IptalIade from "@/pages/legal/IptalIade";
import GizlilikKvkk from "@/pages/legal/GizlilikKvkk";
import PaymentResult from "@/pages/PaymentResult";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminBusinesses from "@/pages/admin/AdminBusinesses";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminReports from "@/pages/admin/AdminReports";
import AdminReservations from "@/pages/admin/AdminReservations";
import { Loader2 } from "lucide-react";

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

function RequireRole({ role, children }) {
  const { user } = useAuth();
  if (user === null) return <Spinner />;
  if (!user) return <Navigate to="/giris" replace />;
  if (role && user.role !== role)
    return <Navigate to={user.role === "super_admin" ? "/admin" : "/panel"} replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Spinner />;
  if (user) {
    if (user.role === "super_admin") return <Navigate to="/admin" replace />;
    const billing = new URLSearchParams(location.search).get("billing");
    if (billing) return <Navigate to={`/panel/abonelik?billing=${billing}`} replace />;
    return <Navigate to="/panel" replace />;
  }
  return children;
}

function MetaPixelTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  return null;
}

function ScrollManager() {
  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    if (!window.location.hash) window.scrollTo(0, 0);
  }, []);
  return null;
}

function RootRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/isletmeler" element={<Businesses />} />
      <Route path="/giris" element={<GuestOnly><Login /></GuestOnly>} />
      <Route path="/kayit" element={<GuestOnly><Register /></GuestOnly>} />
      <Route path="/r/:slug" element={<BookingPage />} />
      <Route path="/randevu/:token" element={<BookingManage />} />
      <Route path="/iletisim" element={<Iletisim />} />
      <Route path="/mesafeli-satis-sozlesmesi" element={<MesafeliSatis />} />
      <Route path="/teslimat-kosullari" element={<Teslimat />} />
      <Route path="/iptal-iade-kosullari" element={<IptalIade />} />
      <Route path="/gizlilik-kvkk" element={<GizlilikKvkk />} />
      <Route path="/odeme/sonuc" element={<PaymentResult />} />
      <Route path="/panel" element={<RequireRole role="owner"><DashboardLayout /></RequireRole>}>
        <Route index element={<Dashboard />} />
        <Route path="hizmetler" element={<Services />} />
        <Route path="personel" element={<Staff />} />
        <Route path="raporlar" element={<Reports />} />
        <Route path="kasa" element={<CashRegister />} />
        <Route path="calisma-saatleri" element={<WorkingHours />} />
        <Route path="kapali-tarihler" element={<Blockouts />} />
        <Route path="rezervasyonlar" element={<Reservations />} />
        <Route path="takvim" element={<Takvim />} />
        <Route path="abonelik" element={<Subscription />} />
        <Route path="ayarlar" element={<Settings />} />
        <Route path="whatsapp" element={<WhatsAppSettings />} />
      </Route>
      <Route path="/admin" element={<RequireRole role="super_admin"><AdminLayout /></RequireRole>}>
        <Route index element={<AdminDashboard />} />
        <Route path="isletmeler" element={<AdminBusinesses />} />
        <Route path="rezervasyonlar" element={<AdminReservations />} />
        <Route path="planlar" element={<AdminPlans />} />
        <Route path="rapor" element={<AdminReports />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Toaster position="top-center" />
      <BrowserRouter>
        <MetaPixelTracker />
        <ScrollManager />
        <RootRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
